<template>
  <div>
    <div v-if="item">
      <EmptyPageContent
        v-if="!item.isEnabled"
        :text="{
          title: $t(item.text.title),
          description: $t(item.text.description),
          btnPrimary: $t(item.text.btnPrimary)
        }"
        :moreLink="{
          text: $t(item.text.btnSecondary),
          url: $t(item.hrefLink)
        }"
        :imageName="item.imageName"
        @click="handleClick"
        v-test="'marketing-view-empty-state'"
      >
        <template #visual>
          <RebookDisabledVisual v-if="item.routeName === 'rebook-reminder'" />
        </template>
      </EmptyPageContent>
      <component
        :is="item.component"
        v-else
      />
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, markRaw, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import onUpgradeButtonClick from '@/modules/_shared/subscription/on-upgrade-button-click';
import RebookDisabledVisual from '@/modules/marketing/email/templates/rebook-reminder/RebookDisabledVisual.vue';
import { useUserStore } from '@/stores/user';
import DiscountIndex from './discounts/index.vue';
import EmailIndex from './email/index.vue';
import FilterIndex from './filter-groups/index.vue';
import RebookIndex from './rebook-reminder/index.vue';
import WaitingListIndex from './waiting-list/index.vue';

const { hasFeatureFlag } = useUserStore();
const { handleUpgradeButtonClick } = onUpgradeButtonClick();
const props = defineProps<{ route: string }>();

const item = computed(() =>
  marketingArray.find((item: any) => item.routeName === props.route),
);

const { t } = useI18n();

const handleClick = (button: string) => {
  if (button === 'primary') {
    handleUpgradeButtonClick();
  }
};

const marketingArray = reactive([
  {
    routeName: 'discounts',
    isEnabled: hasFeatureFlag('module-marketing'),
    text: {
      title: 'marketing.emptyStates.discounts.title',
      description: 'marketing.emptyStates.discounts.body',
      btnPrimary: 'global.upgrade_now',
      btnSecondary: 'global.learn_more',
    },
    hrefLink: helpCenterArticleUrl(t('marketing.insights.tips.lmd.link')),
    imageName: 'discounts',
    component: markRaw(DiscountIndex),
  },
  {
    routeName: 'email',
    isEnabled: hasFeatureFlag('module-marketing'),
    text: {
      title: 'marketing.emptyStates.email.title',
      description: 'marketing.emptyStates.email.body',
      btnPrimary: 'global.upgrade_now',
      btnSecondary: 'global.learn_more',
    },
    hrefLink: helpCenterArticleUrl(t('marketing.insights.tips.email.link')),
    imageName: 'email',
    component: markRaw(EmailIndex),
  },
  {
    routeName: 'rebook-reminder',
    isEnabled: hasFeatureFlag('module-marketing'),
    text: {
      title: 'marketing.emptyStates.rebook.title',
      description: 'marketing.emptyStates.rebook.body',
      btnPrimary: 'global.upgrade_now',
      btnSecondary: 'global.learn_more',
    },
    hrefLink: helpCenterArticleUrl(t('marketing.insights.tips.rebook.link')),
    component: markRaw(RebookIndex),
  },
  {
    routeName: 'waiting-list',
    isEnabled: hasFeatureFlag('module-waiting-list'),
    text: {
      title: 'marketing.emptyStates.waitingList.title',
      description: 'marketing.emptyStates.waitingList.body',
      btnPrimary: 'global.upgrade_now',
      btnSecondary: 'global.learn_more',
    },
    hrefLink: helpCenterArticleUrl(
      t('marketing.insights.tips.waiting-list.link'),
    ),
    imageName: 'marketing-waitinglist',
    component: markRaw(WaitingListIndex),
  },
  {
    routeName: 'filter',
    isEnabled: hasFeatureFlag('module-marketing'),
    text: {
      title: 'marketing.emptyStates.filter.title',
      description: 'marketing.emptyStates.filter.body',
      btnPrimary: 'global.upgrade_now',
      btnSecondary: 'global.learn_more',
    },
    hrefLink: helpCenterArticleUrl(
      t('marketing.insights.tips.filter-groups.link'),
    ),
    imageName: 'filters',
    component: markRaw(FilterIndex),
  },
]);
</script>
